import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import tableStyles from '../../../components/tables/table.module.scss';
// Namerno delimo stilove sa tabelom terapija - prikaz mesecne aktivnosti je isti.
import styles from './TherapiesExpandableTable.module.scss';
import StatusFlag from '../../../components/elements/StatusFlag';
import { getOccurrenceStatusTone } from '../../../shared/utils/statusTones';
import { AdvanceReservationsPanel } from './ReservationBadges';
import { getOccurrenceStatusDisplayLabel } from '../utils/therapyOccurrencePresentation';

// Rezervisane aktivnosti nemaju kolonu "Obavestenje", pa je grid uzi nego na terapijama.
const activityColumnsStyle = { gridTemplateColumns: '3fr 7fr 8fr' };

const getPatientMedicationsLabel = (patient) => {
  const medications = Array.isArray(patient?.medications)
    ? patient.medications
    : [];

  return medications.length ? medications.join(', ') : '-';
};

const getPatientMedicationsShortLabel = (patient) => {
  const medications = Array.isArray(patient?.medications)
    ? patient.medications
    : [];

  return medications.length
    ? medications.map((medication) => medication.split(' ')[0]).join(', ')
    : '-';
};

const ReservedTherapiesTable = ({
  data = [],
  onPatientClick,
  emptyState = {
    icon: '/assets/icons/no-users.svg',
    title: 'Trenutno nema rezervisanih terapija',
  },
  tableMinWidth = 1100,
}) => (
  <div className={styles.tableWrapper}>
    <table
      style={{ width: '100%', minWidth: tableMinWidth ?? undefined }}
      className={styles.therapiesTable}
    >
      <colgroup>
        <col style={{ width: '12%' }} />
        <col style={{ width: '12%' }} />
        <col style={{ width: '11%' }} />
        <col style={{ width: '11%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '8%' }} />
        <col style={{ width: '14%' }} />
        <col style={{ width: '18%' }} />
      </colgroup>
      <thead>
        <tr>
          <th rowSpan={2}>Prezime</th>
          <th rowSpan={2}>Ime</th>
          <th rowSpan={2}>LBO</th>
          <th rowSpan={2}>BZK</th>
          <th rowSpan={2}>Lekovi</th>
          <th colSpan={3} className={styles.groupHeader}>
            {'Mesečna aktivnost'}
          </th>
        </tr>
        <tr>
          <th className={styles.subHeader}>Mesec</th>
          <th className={styles.subHeader}>Datum</th>
          <th className={styles.subHeader}>Status</th>
        </tr>
      </thead>
      <tbody>
        {data.map((patient, index) => {
          const occurrences = Array.isArray(patient?.reservedOccurrences)
            ? patient.reservedOccurrences
            : [];
          const advanceReservations = Array.isArray(
            patient?.advanceReservations
          )
            ? patient.advanceReservations
            : [];
          const medicationsLabel = getPatientMedicationsLabel(patient);

          return (
            <tr key={patient?.userId || index}>
              <td className={styles.topAlignedCell}>
                <span
                  className={styles.wrapText}
                  title={patient?.lastName || '-'}
                  role="button"
                  tabIndex={0}
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => onPatientClick?.(patient)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      onPatientClick?.(patient);
                    }
                  }}
                >
                  {patient?.lastName || '-'}
                </span>
              </td>
              <td className={styles.topAlignedCell}>
                <span
                  className={styles.wrapText}
                  title={patient?.firstName || '-'}
                >
                  {patient?.firstName || '-'}
                </span>
              </td>
              <td className={styles.topAlignedCell}>
                <span
                  className={styles.wrapText}
                  title={patient?.userLbo || '-'}
                >
                  {patient?.userLbo || '-'}
                </span>
              </td>
              <td className={styles.topAlignedCell}>
                <span
                  className={styles.wrapText}
                  title={patient?.healthBookletNumber || '-'}
                >
                  {patient?.healthBookletNumber || '-'}
                </span>
              </td>
              <td className={`${styles.topAlignedCell} ${styles.activityCell}`}>
                <span className={styles.wrapText} title={medicationsLabel}>
                  {getPatientMedicationsShortLabel(patient)}
                </span>
              </td>
              <td
                colSpan={3}
                className={`${styles.topAlignedCell} ${styles.activityGroupCell}`}
              >
                {advanceReservations.length > 0 && (
                  <AdvanceReservationsPanel reservations={advanceReservations} />
                )}
                {/* Tri situacije: samo unapred (panel gore, bez grida) /
                    aktuelno + unapred (panel + grid sa oznakom) / samo aktuelno. */}
                {occurrences.length > 0 ? (
                  <>
                    {advanceReservations.length > 0 && (
                      <div className={styles.reservedSectionLabel}>
                        Aktuelno rezervisano
                      </div>
                    )}
                    <div
                      className={styles.activityColumns}
                      style={activityColumnsStyle}
                    >
                      <div className={styles.activityColumn}>
                        {occurrences.length ? (
                          <div className={styles.activityList}>
                            {occurrences.map((occurrence, occurrenceIndex) => (
                              <div
                                key={`month-${occurrence?._id || occurrenceIndex}`}
                                className={styles.activityItem}
                              >
                                <span
                                  className={styles.nowrapText}
                                  style={{ textAlign: 'center' }}
                                >
                                  {occurrence?.sequenceNumber || '-'}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={styles.emptyActivity}>-</div>
                        )}
                      </div>
                      <div className={styles.activityColumn}>
                        {occurrences.length ? (
                          <div className={styles.activityList}>
                            {occurrences.map((occurrence, occurrenceIndex) => (
                              <div
                                key={`date-${occurrence?._id || occurrenceIndex}`}
                                className={styles.activityItem}
                              >
                                <span className={styles.nowrapText}>
                                  {occurrence?.scheduledDate
                                    ? dayjs(occurrence.scheduledDate).format(
                                        'DD.MM.YY'
                                      )
                                    : '-'}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={styles.emptyActivity}>-</div>
                        )}
                      </div>
                      <div className={styles.activityColumn}>
                        {occurrences.length ? (
                          <div className={styles.activityList}>
                            {occurrences.map((occurrence, occurrenceIndex) => {
                              const statusLabel =
                                getOccurrenceStatusDisplayLabel(occurrence);

                              return (
                                <div
                                  key={`status-${
                                    occurrence?._id || occurrenceIndex
                                  }`}
                                  className={styles.activityItem}
                                >
                                  <StatusFlag
                                    tone={getOccurrenceStatusTone(occurrence?.status)}
                                    title={[statusLabel, occurrence?.therapyName]
                                      .filter(Boolean)
                                      .join(' - ')}
                                  >
                                    {statusLabel}
                                  </StatusFlag>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className={styles.emptyActivity}>-</div>
                        )}
                      </div>
                    </div>
                  </>
                ) : advanceReservations.length === 0 ? (
                  <div className={styles.emptyActivity}>-</div>
                ) : null}
              </td>
            </tr>
          );
        })}
        {data.length === 0 && (
          <tr>
            <td colSpan={8} style={{ textAlign: 'center', padding: 8 }}>
              <div className={tableStyles.noData}>
                <img src={emptyState.icon} alt="Nema podataka" />
                <h3>{emptyState.title}</h3>
              </div>
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

ReservedTherapiesTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  onPatientClick: PropTypes.func,
  emptyState: PropTypes.shape({
    icon: PropTypes.string,
    title: PropTypes.string,
  }),
  tableMinWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

export default ReservedTherapiesTable;
